import type {
  ImportPlayerInput,
  ImportRoom,
  ImportSeasonPlayer,
} from '@sportninja/common/services/teamBalance'
import { isValidEmail } from './inviteForm'

/*
 * Jeff, Oct 1 — Import Players From Previous Seasons: the dialog's rules, kept out
 * of the components so each one can be tested on its own.
 *
 * The rep ticks who to invite. A player free to invite has an address on file; a
 * player with none can be ticked only once a valid one is typed for them; everyone
 * else (the rep, already registered, on the roster, already invited) is listed,
 * greyed, and can't be ticked. The ticks, the typed addresses and the errors live
 * here, never in the grid's row data: typing must not change a row, or the grid
 * would redraw the field under the cursor.
 */

/** The server's own limit on one Send. */
export const MAX_IMPORT = 60

const GREYED: ReadonlyArray<ImportSeasonPlayer['status']> = [
  'team_rep',
  'registered',
  'on_roster',
  'invited',
]

/** Already on this season's sheet: listed, never tickable. */
export const isGreyed = (player: ImportSeasonPlayer): boolean =>
  GREYED.includes(player.status)

/** Could be invited: free to, or once an address is typed for them. */
export const isSelectable = (player: ImportSeasonPlayer): boolean =>
  player.status === 'available' || player.status === 'no_email'

type Flags = Readonly<Record<string, true>>
type Texts = Readonly<Record<string, string>>

export interface ImportSelectionState {
  /** The season these choices belong to: another season starts afresh. */
  seasonId: string | null
  ticked: Flags
  /** Rows the rep unticked: a valid address typed later leaves them unticked. */
  userUnticked: Flags
  /** Addresses typed for players with none on file, as typed. */
  drafts: Texts
  /** Fields the rep left holding something that isn't an address. */
  leftInvalid: Flags
  /** The server's refusal of a row, by player id. */
  rowErrors: Texts
  /** The server's refusal of the whole Send: the room, the lock, approval. */
  formMessage: string | null
  /** This season's first load ticked its reachable regulars; never again. */
  defaultsApplied: boolean
  /** A Send is on its way: nothing can be ticked, typed or switched. */
  pending: boolean
  /** The players the last Send named, in its order: what `players.N` means. */
  submitted: readonly ImportPlayerInput[] | null
}

export type ImportSelectionAction =
  | { type: 'season'; seasonId: string | null }
  | {
      type: 'loaded'
      seasonId: string | null
      players: readonly ImportSeasonPlayer[]
    }
  | { type: 'toggle'; player: ImportSeasonPlayer; checked: boolean }
  | { type: 'toggleAll'; players: readonly ImportSeasonPlayer[] }
  | { type: 'typeEmail'; player: ImportSeasonPlayer; email: string }
  | { type: 'blurEmail'; playerId: string }
  | { type: 'submit'; sent: readonly ImportPlayerInput[] }
  | {
      type: 'refused'
      invalidFields: Record<string, unknown> | null | undefined
      fallbackMessage: string
    }
  | { type: 'settled' }

export const initialImportSelection = (
  seasonId: string | null
): ImportSelectionState => ({
  seasonId,
  ticked: {},
  userUnticked: {},
  drafts: {},
  leftInvalid: {},
  rowErrors: {},
  formMessage: null,
  defaultsApplied: false,
  pending: false,
  submitted: null,
})

const withFlag = (flags: Flags, id: string): Flags =>
  flags[id] ? flags : { ...flags, [id]: true }

const without = <T>(
  map: Readonly<Record<string, T>>,
  id: string
): Readonly<Record<string, T>> => {
  if (!(id in map)) {
    return map
  }
  const { [id]: _dropped, ...rest } = map
  return rest
}

const keep = <T>(
  map: Readonly<Record<string, T>>,
  stays: (id: string) => boolean
): Readonly<Record<string, T>> =>
  Object.fromEntries(Object.entries(map).filter(([id]) => stays(id)))

const sameEntries = <T>(
  a: Readonly<Record<string, T>>,
  b: Readonly<Record<string, T>>
): boolean => {
  const keys = Object.keys(a)
  return (
    keys.length === Object.keys(b).length &&
    keys.every((key) => key in b && a[key] === b[key])
  )
}

/**
 * Has an address to be invited at: the one on file, or a valid one typed for a
 * player with none.
 */
const hasAddress = (player: ImportSeasonPlayer, drafts: Texts): boolean =>
  player.status === 'available'
    ? Boolean(player.email)
    : player.status === 'no_email' && isValidEmail(drafts[player.id] ?? '')

/** Can be ticked right now. */
export const isInviteable = (
  player: ImportSeasonPlayer,
  state: Pick<ImportSelectionState, 'drafts'>
): boolean => isSelectable(player) && hasAddress(player, state.drafts)

/** Reachable regulars start ticked; spares and players without an address don't. */
const defaultTicks = (players: readonly ImportSeasonPlayer[]): Flags =>
  Object.fromEntries(
    players
      .filter(
        (player) =>
          player.status === 'available' && !player.is_spare && player.email
      )
      .map((player) => [player.id, true as const])
  )

/**
 * The server's 422 mapped back onto the rows that were sent: `players.N`,
 * `players.N.id` and `players.N.email` name the Nth player of THAT Send, however
 * the grid has changed since. Anything else (the room, the lock, approval) is the
 * whole dialog's.
 */
export const mapImportErrors = (
  invalidFields: Record<string, unknown> | null | undefined,
  sent: readonly ImportPlayerInput[]
): { rowErrors: Record<string, string>; formMessage: string | null } => {
  const rowErrors: Record<string, string> = {}
  let formMessage: string | null = null

  for (const [key, value] of Object.entries(invalidFields ?? {})) {
    const message = Array.isArray(value)
      ? String(value[0] ?? '')
      : String(value ?? '')
    if (!message) {
      continue
    }
    const row = /^players\.(\d+)(?:\.(?:id|email))?$/.exec(key)
    const playerId = row ? sent[Number(row[1])]?.id : undefined
    if (playerId) {
      rowErrors[playerId] ??= message
    } else if (formMessage === null) {
      formMessage = message
    }
  }

  return { rowErrors, formMessage }
}

export const importSelectionReducer = (
  state: ImportSelectionState,
  action: ImportSelectionAction
): ImportSelectionState => {
  switch (action.type) {
    case 'season':
      return action.seasonId === state.seasonId
        ? state
        : initialImportSelection(action.seasonId)

    case 'loaded': {
      // A late answer for a season the rep has already left changes nothing.
      if (action.seasonId !== state.seasonId) {
        return state
      }
      if (!state.defaultsApplied) {
        return {
          ...state,
          ticked: defaultTicks(action.players),
          defaultsApplied: true,
        }
      }
      // A refetch: what the rep did stays for the players still listed; a tick
      // goes once its player can't be invited any more (someone else invited
      // them, they registered) or has gone. Defaults are never applied again.
      const listed = new Map(
        action.players.map((player) => [player.id, player])
      )
      const isListed = (id: string) => listed.has(id)
      const next = {
        ticked: keep(state.ticked, (id) => {
          const player = listed.get(id)
          return player !== undefined && isInviteable(player, state)
        }),
        userUnticked: keep(state.userUnticked, isListed),
        drafts: keep(state.drafts, isListed),
        leftInvalid: keep(state.leftInvalid, isListed),
        rowErrors: keep(state.rowErrors, isListed),
      }
      // The same answer again changes nothing, and must not re-render the grid.
      const unchanged =
        sameEntries(next.ticked, state.ticked) &&
        sameEntries(next.userUnticked, state.userUnticked) &&
        sameEntries(next.drafts, state.drafts) &&
        sameEntries(next.leftInvalid, state.leftInvalid) &&
        sameEntries(next.rowErrors, state.rowErrors)

      return unchanged ? state : { ...state, ...next }
    }

    case 'toggle': {
      if (state.pending) {
        return state
      }
      const { player, checked } = action
      if (!checked) {
        return {
          ...state,
          ticked: without(state.ticked, player.id),
          userUnticked: withFlag(state.userUnticked, player.id),
        }
      }
      if (!isInviteable(player, state)) {
        return state
      }
      return {
        ...state,
        ticked: withFlag(state.ticked, player.id),
        userUnticked: without(state.userUnticked, player.id),
      }
    }

    case 'toggleAll': {
      if (state.pending) {
        return state
      }
      const pool = action.players.filter((player) =>
        isInviteable(player, state)
      )
      if (pool.length === 0) {
        return state
      }
      const allTicked = pool.every((player) => state.ticked[player.id])
      const ticked = { ...state.ticked }
      const userUnticked = { ...state.userUnticked }
      for (const { id } of pool) {
        if (allTicked) {
          delete ticked[id]
          userUnticked[id] = true
        } else {
          ticked[id] = true
          delete userUnticked[id]
        }
      }
      return { ...state, ticked, userUnticked }
    }

    case 'typeEmail': {
      const { player, email } = action
      if (state.pending || player.status !== 'no_email') {
        return state
      }
      const valid = isValidEmail(email)
      // Typing a valid address ticks the row, unless the rep unticked it; until
      // it is valid the row can't be ticked at all.
      const ticked = valid
        ? state.userUnticked[player.id]
          ? state.ticked
          : withFlag(state.ticked, player.id)
        : without(state.ticked, player.id)

      return {
        ...state,
        drafts: { ...state.drafts, [player.id]: email },
        ticked,
        leftInvalid: without(state.leftInvalid, player.id),
        rowErrors: without(state.rowErrors, player.id),
      }
    }

    case 'blurEmail': {
      const draft = (state.drafts[action.playerId] ?? '').trim()
      return draft !== '' && !isValidEmail(draft)
        ? {
            ...state,
            leftInvalid: withFlag(state.leftInvalid, action.playerId),
          }
        : state
    }

    case 'submit':
      return {
        ...state,
        pending: true,
        submitted: action.sent,
        rowErrors: {},
        formMessage: null,
      }

    case 'refused': {
      const mapped = mapImportErrors(
        action.invalidFields,
        state.submitted ?? []
      )
      const anyRow = Object.keys(mapped.rowErrors).length > 0

      return {
        ...state,
        pending: false,
        rowErrors: mapped.rowErrors,
        formMessage:
          mapped.formMessage ?? (anyRow ? null : action.fallbackMessage),
      }
    }

    case 'settled':
      return { ...state, pending: false }

    default:
      return state
  }
}

/**
 * The grid's order: who can be invited, then players still needing an address,
 * then spares, then everyone already on the sheet; by name within each. It reads
 * only what the server says, so a row never moves while the rep types.
 */
export const sortImportPlayers = (
  players: readonly ImportSeasonPlayer[]
): ImportSeasonPlayer[] => {
  const rank = (player: ImportSeasonPlayer) =>
    isGreyed(player)
      ? 3
      : player.is_spare
      ? 2
      : player.status === 'no_email'
      ? 1
      : 0

  return [...players].sort(
    (a, b) =>
      rank(a) - rank(b) ||
      (a.name_last ?? '').localeCompare(b.name_last ?? '') ||
      (a.name_first ?? '').localeCompare(b.name_first ?? '')
  )
}

/** A player with no address on file still needs a valid one typed. */
export const needsEmail = (
  player: ImportSeasonPlayer,
  typed: string | undefined
): boolean => player.status === 'no_email' && !isValidEmail(typed ?? '')

export interface ImportSummary {
  /** Ticked players who can be invited: what Send N Invites sends. */
  count: number
  /** Every player still without an address, ticked or not. */
  needEmail: number
  overCap: boolean
  /** How many to untick to fit the division's cap; 0 when it fits. */
  overRoomBy: number
  /** Places left after this Send; null when the division caps nobody. */
  roomLeft: number | null
  /** Nobody on the season can be invited any more. */
  everyoneIn: boolean
  /** The header box: all, some or none of the players who can be ticked. */
  header: { checked: boolean; indeterminate: boolean; disabled: boolean }
}

export const importSummary = (
  players: readonly ImportSeasonPlayer[],
  state: ImportSelectionState,
  room: ImportRoom | null
): ImportSummary => {
  const pool = players.filter((player) => isInviteable(player, state))
  const count = pool.filter((player) => state.ticked[player.id]).length

  return {
    count,
    needEmail: players.filter((player) =>
      needsEmail(player, state.drafts[player.id])
    ).length,
    overCap: count > MAX_IMPORT,
    overRoomBy: room ? Math.max(0, count - room.remaining) : 0,
    roomLeft: room ? Math.max(0, room.remaining - count) : null,
    everyoneIn: players.length > 0 && !players.some(isSelectable),
    header: {
      checked: pool.length > 0 && count === pool.length,
      indeterminate: count > 0 && count < pool.length,
      disabled: pool.length === 0 || state.pending,
    },
  }
}

/** The ticked players as the API takes them, in the grid's order. */
export const toImportPlayers = (
  players: readonly ImportSeasonPlayer[],
  state: ImportSelectionState
): ImportPlayerInput[] =>
  players
    .filter((player) => state.ticked[player.id] && isInviteable(player, state))
    .map((player) =>
      player.status === 'no_email'
        ? {
            id: player.id,
            email: (state.drafts[player.id] ?? '').trim().toLowerCase(),
          }
        : { id: player.id }
    )
